// Monte Carlo tab of the Assessment page (src/pages/Assessment.js).
// Pure code-motion (Tech Debt 2026-09 "Assessment and Backtest are god
// files" ticket) -- self-contained: its own fetch (/api/mc/results) and
// loading/error state, matching the ticket's target shape. No behaviour
// change from the version formerly inline in Assessment.js.
import React, { useState, useEffect } from 'react';
import { Activity } from 'lucide-react';
import { cacheBustBucket, fmtSignedMagnitude, ScrollTable } from './shared';

// ── Monte Carlo tab ────────────────────────────────────────────────────────
// Surfaces the canonical N-iteration MC run table for the active version
// (or a selected historical version). DD-primary view with a 5y row pinned
// at the top; per-window WorstDD color-coded against the 80% ship floor.
// Data comes from /api/mc/results, populated by monte_carlo.py upserts.

const MC_WINDOW_ORDER = ['5y', '22-now', '2025', 'dip', '2024', '2023', '2022', '2021'];

function fmtRetMC(v) {
  if (v == null || !isFinite(v)) return '—';
  return fmtSignedMagnitude(v, {
    decimals: 1,
    suffix: '%',
    compactSuffix: 'k',
    forcePlusZero: true,
  }) || '—';
}

function fmtMcPct(v, dp = 1) {
  if (v == null || !isFinite(v)) return '—';
  return `${v.toFixed(dp)}%`;
}

function fmtMcDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return '—'; }
}

function ddColor(pct) {
  if (pct == null) return 'text-gray-700';
  if (pct >= 80) return 'text-trading-red-300 font-semibold';      // floor breach
  if (pct >= 75) return 'text-amber-300';
  if (pct >= 70) return 'text-amber-200';
  if (pct >= 60) return 'text-gray-200';
  return 'text-trading-green-300';
}

function collapseColor(pct) {
  if (pct == null) return 'text-gray-700';
  if (pct === 0)   return 'text-trading-green-300';
  if (pct < 5)     return 'text-amber-300';
  return 'text-trading-red-300 font-semibold';
}

function tpColor(pct, be) {
  if (pct == null) return 'text-gray-700';
  if (pct < be)        return 'text-trading-red-300';
  if (pct < be + 5)    return 'text-amber-200';
  if (pct < be + 10)   return 'text-gray-200';
  return 'text-trading-green-300';
}

export default function MonteCarloTab({ apiBase, dteStrategy = '30', versionId, refreshNonce = 0 }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState(null);
  const [nIterMin, setNIterMin] = useState(0);  // 0 = all, 300/500 filters

  useEffect(() => {
    setLoading(true);
    setErr(null);
    setData(null);
    const params = new URLSearchParams();
    params.set('dte', dteStrategy);
    if (versionId) params.set('version', versionId);
    if (nIterMin > 0) params.set('n_iter', String(nIterMin));
    params.set('_t', cacheBustBucket());
    fetch(`${apiBase}/api/mc/results?${params.toString()}`)
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d.error || 'fetch failed');
        setData(d);
        setLoading(false);
      })
      .catch(e => { setErr(e.message); setLoading(false); });
  }, [apiBase, dteStrategy, versionId, nIterMin, refreshNonce]);

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-gray-500 text-sm gap-2">
      <Activity className="w-4 h-4 animate-spin" /> Loading…
    </div>
  );
  if (err) return (
    <div className="py-10 text-center space-y-2">
      <p className="text-trading-red-400 font-medium">Failed to load MC results</p>
      <p className="text-gray-500 text-sm">{err}</p>
    </div>
  );

  const runs    = data?.runs || [];
  const summary = data?.summary || {};
  const order   = data?.window_order || MC_WINDOW_ORDER;

  // Sort runs by canonical window order, then n_iter desc, then run_at desc
  const orderedRuns = [...runs].sort((a, b) => {
    const ai = order.indexOf(a.window_label);
    const bi = order.indexOf(b.window_label);
    if (ai !== bi) return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    if ((a.n_iter || 0) !== (b.n_iter || 0)) return (b.n_iter || 0) - (a.n_iter || 0);
    return (b.run_at || '').localeCompare(a.run_at || '');
  });

  // Headline 5y summary for the stat card
  const fiveY = summary['5y'];
  // What the DD columns measure (API dd_basis): 'mtm' = mark-to-market (runs from
  // 2026-09-27 on); 'cost' = legacy cost-basis runs (open options held at premium paid).
  // (A payload without dd_basis predates the field -> cost basis.)
  const ddBasisLabel = data?.dd_basis === 'mtm' ? 'mark-to-market'
    : data?.dd_basis === 'all' ? 'mixed bases (see each run)' : 'cost basis (legacy runs)';

  // Empty state
  if (orderedRuns.length === 0) {
    return (
      <div className="space-y-4">
        <div className="bg-trading-dark-900 border border-white/[0.06] rounded p-6 space-y-3">
          <h3 className="text-[14px] font-medium text-white">No Monte Carlo data yet for this version</h3>
          <p className="text-[12px] text-gray-400 leading-relaxed">
            MC results are persisted automatically when <span className="font-mono text-gray-300">monte_carlo.py</span> or
            {' '}<span className="font-mono text-gray-300">monte_carlo_15dte.py</span> runs to completion.
            Each window upserts on (version × dte × params_hash) so re-runs replace the prior row.
          </p>
          <div className="bg-black/40 border border-white/[0.04] rounded px-3 py-2 font-mono text-[11px] text-gray-300 space-y-1">
            <div># 30 DTE, full N=500 × 8 windows (canonical ship gate)</div>
            <div className="text-trading-blue-300">PYTHONIOENCODING=utf-8 python -u monte_carlo.py</div>
            <div className="pt-1"># 15 DTE</div>
            <div className="text-trading-blue-300">PYTHONIOENCODING=utf-8 python -u monte_carlo_15dte.py</div>
            <div className="pt-1"># Smoke (N=100, single window)</div>
            <div className="text-trading-blue-300">N_ITER_OVERRIDE=100 WINDOWS_OVERRIDE=22-now python -u monte_carlo.py</div>
          </div>
          <p className="text-[10px] text-gray-600">
            Results land in the <span className="font-mono">monte_carlo_run</span> table.
            Set <span className="font-mono">MC_NO_DB_PERSIST=1</span> on a sweep run to skip persistence.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Headline stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-trading-dark-900 border border-white/[0.06] rounded px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.14em] text-gray-600 mb-1">5y WorstDD</p>
          <p className={`text-[16px] font-semibold font-mono tabular-nums ${ddColor(fiveY?.worst_dd_pct)}`}>
            {fiveY?.worst_dd_pct != null ? `${fiveY.worst_dd_pct.toFixed(1)}%` : '—'}
          </p>
          <p className="text-[10px] text-gray-600 mt-0.5">primary ship gate · floor 80% · {ddBasisLabel}</p>
        </div>
        <div className="bg-trading-dark-900 border border-white/[0.06] rounded px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.14em] text-gray-600 mb-1">5y MedRet</p>
          <p className="text-[16px] font-semibold font-mono tabular-nums text-gray-200">
            {fmtRetMC(fiveY?.med_ret_pct)}
          </p>
          <p className="text-[10px] text-gray-600 mt-0.5">non-regression sanity · ±3 OOMs</p>
        </div>
        <div className="bg-trading-dark-900 border border-white/[0.06] rounded px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.14em] text-gray-600 mb-1">5y P(collapse)</p>
          <p className={`text-[16px] font-semibold font-mono tabular-nums ${collapseColor(fiveY?.collapse_rate_pct)}`}>
            {fmtMcPct(fiveY?.collapse_rate_pct)}
          </p>
          <p className="text-[10px] text-gray-600 mt-0.5">Stage 3 T6 · must be 0%</p>
        </div>
        <div className="bg-trading-dark-900 border border-white/[0.06] rounded px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.14em] text-gray-600 mb-1">N (5y)</p>
          <p className="text-[16px] font-semibold font-mono tabular-nums text-gray-200">
            {fiveY?.n_iter ?? '—'}
          </p>
          <p className="text-[10px] text-gray-600 mt-0.5">
            {fiveY?.engine_version ?? 'no run'} · {fmtMcDate(fiveY?.run_at)}
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 text-[11px]">
        <span className="text-gray-500 uppercase tracking-[0.14em]">Min N</span>
        <div className="flex items-center border border-white/[0.06] rounded-md overflow-hidden">
          {[
            { k: 0,   lbl: 'All' },
            { k: 100, lbl: '≥100' },
            { k: 300, lbl: '≥300' },
            { k: 500, lbl: '≥500' },
          ].map(({ k, lbl }, i) => (
            <button
              key={k}
              onClick={() => setNIterMin(k)}
              className={`px-2.5 py-1 font-medium transition-colors whitespace-nowrap ${
                i > 0 ? 'border-l border-white/[0.06]' : ''
              } ${nIterMin === k ? 'bg-white/[0.06] text-white' : 'text-gray-500 hover:text-gray-300'}`}
            >
              {lbl}
            </button>
          ))}
        </div>
        <span className="text-gray-600 ml-auto">
          {orderedRuns.length} run{orderedRuns.length === 1 ? '' : 's'}
          {' · '}engine: {orderedRuns[0]?.engine_version || '—'}
        </span>
      </div>

      {/* Per-window MC table */}
      <ScrollTable>
        <table className="w-full text-[12px] tabular-nums font-mono">
          <thead className="bg-white/[0.02] border-b border-white/[0.06]">
            <tr className="text-gray-500 text-[10px] uppercase tracking-[0.14em]">
              <th className="text-left px-3 py-2 font-medium">Window</th>
              <th className="text-right px-2 py-2 font-medium">N</th>
              <th className="text-right px-2 py-2 font-medium" title="Median return across iterations (P50). Robust against lognormal skew.">
                MedRet
              </th>
              <th className="text-right px-2 py-2 font-medium text-gray-700"
                  title="Mean return across iterations. Skewed by best tails on lognormal distribution. Reference only.">
                MeanRet
              </th>
              <th className="text-right px-2 py-2 font-medium text-trading-red-300/80"
                  title={`Worst DD across iterations (${ddBasisLabel}) — Stage 3 T4 ship gate. Floor at 80%.`}>
                WorstDD
              </th>
              <th className="text-right px-2 py-2 font-medium" title={`Mean DD across iterations (${ddBasisLabel}).`}>MeanDD</th>
              <th className="text-right px-2 py-2 font-medium text-trading-red-300/80"
                  title="P(collapse) — fraction of iterations where portfolio < 20% of starting cash. Stage 3 T6: must be 0%.">
                Coll%
              </th>
              <th className="text-right px-2 py-2 font-medium" title="Mean call TP rate across iterations.">CallTP</th>
              <th className="text-right px-2 py-2 font-medium" title="Mean put TP rate across iterations.">PutTP</th>
              <th className="text-right px-2 py-2 font-medium" title="Mean # of call trades per iteration.">CTrd</th>
              <th className="text-right px-2 py-2 font-medium" title="Mean # of put trades per iteration.">PTrd</th>
              <th className="text-right px-2 py-2 font-medium">Run</th>
            </tr>
          </thead>
          <tbody>
            {orderedRuns.map(r => {
              const isFiveY = r.window_label === '5y';
              const callBE = 46.2;   // current 30 DTE call BE
              const putBE  = 36.4;   // current put BE
              return (
                <tr key={r.id} className={`border-b border-white/[0.03] hover:bg-white/[0.02] ${
                  isFiveY ? 'bg-trading-blue-500/[0.04]' : ''
                }`}>
                  <td className={`px-3 py-2 font-sans ${isFiveY ? 'text-white font-medium' : 'text-gray-300'}`}>
                    {r.window_label}
                    {isFiveY && <span className="ml-1.5 text-[9px] text-trading-blue-300 uppercase tracking-[0.14em]">primary</span>}
                  </td>
                  <td className="text-right px-2 py-2 text-gray-400">{r.n_iter}</td>
                  <td className="text-right px-2 py-2 text-gray-200">{fmtRetMC(r.med_ret_pct)}</td>
                  <td className="text-right px-2 py-2 text-gray-600">{fmtRetMC(r.mean_ret_pct)}</td>
                  <td className={`text-right px-2 py-2 ${ddColor(r.worst_dd_pct)}`}>{fmtMcPct(r.worst_dd_pct)}</td>
                  <td className="text-right px-2 py-2 text-gray-400">{fmtMcPct(r.mean_dd_pct)}</td>
                  <td className={`text-right px-2 py-2 ${collapseColor(r.collapse_rate_pct)}`}>
                    {fmtMcPct(r.collapse_rate_pct)}
                  </td>
                  <td className={`text-right px-2 py-2 ${tpColor(r.call_tp_rate_pct, callBE)}`}>
                    {fmtMcPct(r.call_tp_rate_pct)}
                  </td>
                  <td className={`text-right px-2 py-2 ${tpColor(r.put_tp_rate_pct, putBE)}`}>
                    {fmtMcPct(r.put_tp_rate_pct)}
                  </td>
                  <td className="text-right px-2 py-2 text-gray-400">{r.n_call_trades?.toFixed(0) ?? '—'}</td>
                  <td className="text-right px-2 py-2 text-gray-400">{r.n_put_trades?.toFixed(0) ?? '—'}</td>
                  <td className="text-right px-2 py-2 text-gray-500 font-sans"
                      title={r.run_at ? new Date(r.run_at).toLocaleString() : ''}>
                    {fmtMcDate(r.run_at)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ScrollTable>

      {/* Methodology footer */}
      <div className="bg-trading-dark-900 border border-white/[0.06] rounded px-4 py-3 space-y-2">
        <p className="text-[11px] text-gray-400 leading-relaxed">
          <span className="text-gray-200 font-medium">How this table is populated:</span>{' '}
          <span className="font-mono">monte_carlo.py</span> and <span className="font-mono">monte_carlo_15dte.py</span> upsert one row per
          {' '}<span className="font-mono">(version × dte × window × params_hash)</span> at the end of each window's iteration loop.
          Re-running the same config replaces the row; changing any param (N, TP/SL, cascade, etc.) produces a new row.
          See <span className="font-mono">database/utils/mc_persist.py</span>.
        </p>
        <p className="text-[11px] text-gray-400 leading-relaxed">
          <span className="text-gray-200 font-medium">Reading the colors:</span>{' '}
          <span className="text-trading-green-300">green</span> = pass / safe ·
          {' '}<span className="text-amber-300">amber</span> = approaching threshold ·
          {' '}<span className="text-trading-red-300">red</span> = floor breach. WorstDD floor = 80% (Stage 3 T4).
          {' '}Drawdowns: {data?.dd_basis === 'mtm'
            ? 'mark-to-market -- open options valued each day at the model mark, as the live Portfolio page does.'
            : 'cost basis -- these runs predate 2026-09-27 and hold open options at the premium paid; re-run for mark-to-market.'}
          {' '}The two bases are never mixed in one view.
          P(collapse) must be 0% (Stage 3 T6). Call TP BE ≈ 46.2%, Put TP BE ≈ 36.4% under current shipped TP/SL.
        </p>
      </div>
    </div>
  );
}
